import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { euro } from '@fleet/shared';
import { Badge, Button } from '../../design-system';
import { AutoLink, PasLink, PersoonLink } from '../../components/ui/EntiteitLink';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { ZoekBalk } from '../../components/ui/ZoekBalk';
import { useArchivedPassen, useDearchiveerPas } from './passen.api';

const COLS = '160px 1fr 130px 120px 120px 150px';

export function PassenArchiefPage() {
  const navigate = useNavigate();
  const { data: passen, isLoading } = useArchivedPassen();
  const dearchiveer = useDearchiveerPas();
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const all = passen ?? [];
    const term = q.trim().toLowerCase();
    if (!term) return all;
    return all.filter((p) =>
      `${p.pasnummer} ${p.persoonNaam ?? ''} ${p.kenteken ?? ''}`.toLowerCase().includes(term),
    );
  }, [passen, q]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/passen')}>
          ← Alle passen
        </Button>
        <div style={{ marginTop: 8 }}>
          <Eyebrow>Archief</Eyebrow>
          <h2 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 900, color: '#111111' }}>
            Passen uit gebruik.
          </h2>
        </div>
      </div>

      <ZoekBalk
        waarde={q}
        onWaarde={setQ}
        placeholder="Zoek op pasnummer, naam of kenteken…"
        ariaLabel="Archief zoeken"
        aantal={
          filtered.length === (passen?.length ?? 0)
            ? `${filtered.length} passen`
            : `${filtered.length} van ${passen?.length ?? 0}`
        }
      />

      <div
        style={{
          background: '#FFFFFF',
          border: '1px solid #E3D9CC',
          borderRadius: 4,
          overflowX: 'auto',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COLS,
            gap: 10,
            padding: '12px 20px',
            borderBottom: '2px solid #111111',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#666666',
            minWidth: 760,
          }}
        >
          <span>Pasnummer</span>
          <span>Persoon</span>
          <span>Kenteken</span>
          <span>Totaal</span>
          <span>Status</span>
          <span style={{ textAlign: 'right' }}>Actie</span>
        </div>

        {isLoading && <div style={{ padding: '24px 20px', color: '#888888' }}>Archief laden…</div>}

        {filtered.map((p) => (
          <div
            key={p.id}
            style={{
              display: 'grid',
              gridTemplateColumns: COLS,
              gap: 10,
              padding: '11px 20px',
              borderBottom: '1px solid #F0E8DC',
              fontSize: 13,
              alignItems: 'center',
              minWidth: 760,
            }}
          >
            <PasLink pasId={p.id} pasnummer={p.pasnummer} style={{ fontSize: 12 }} />
            <PersoonLink
              persoonId={p.persoonId}
              naam={p.persoonNaam}
              style={{ color: '#444444' }}
            />
            <AutoLink kenteken={p.kenteken} style={{ fontSize: 12 }} />
            <span>{p.kostenTotaal ? euro(p.kostenTotaal) : '—'}</span>
            <span>
              <Badge tone="neutral">Uit gebruik</Badge>
            </span>
            <span style={{ textAlign: 'right' }}>
              <Button
                variant="secondary"
                size="sm"
                disabled={dearchiveer.isPending}
                onClick={() => dearchiveer.mutate(p.id)}
              >
                Terug in gebruik
              </Button>
            </span>
          </div>
        ))}

        {!isLoading && filtered.length === 0 && (
          <div style={{ padding: '24px 20px', color: '#888888' }}>
            {q ? 'Geen passen gevonden.' : 'Geen passen in het archief.'}
          </div>
        )}
      </div>
    </div>
  );
}
